import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { BFF_BASE_URL } from '../../auth/authApi';

export interface Collection {
  id: string;
  name: string;
  count: number;
  metadata?: Record<string, unknown>;
}

export interface CollectionsState {
  collections: Collection[];
  loading: boolean;
  error: string | null;
}

export const collectionsInitialState: CollectionsState = {
  collections: [],
  loading: false,
  error: null,
};

export const fetchCollections = createAsyncThunk(
  'collections/fetchCollections',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BFF_BASE_URL}/admin/collections`);
      return response.data.collections;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.error || error.message);
      }
      return rejectWithValue('Failed to fetch collections');
    }
  }
);

const collectionsSlice = createSlice({
  name: 'collections',
  initialState: collectionsInitialState,
  reducers: {
    setCollections: (state, action: PayloadAction<Collection[]>) => {
      state.collections = action.payload;
      state.loading = false;
      state.error = null;
    },
    addCollection: (state, action: PayloadAction<Collection>) => {
      state.collections.push(action.payload);
    },
    updateCollection: (state, action: PayloadAction<Collection>) => {
      const index = state.collections.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.collections[index] = action.payload;
      }
    },
    deleteCollection: (state, action: PayloadAction<string>) => {
      state.collections = state.collections.filter(c => c.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCollections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        state.collections = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCollections,
  addCollection,
  updateCollection,
  deleteCollection,
  setLoading,
  setError,
} = collectionsSlice.actions;

export default collectionsSlice;
